import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Warning } from '@mui/icons-material';

interface ServicePackage {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
}

interface DeletePackageDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  package: ServicePackage | null;
  loading?: boolean;
}

const DeletePackageDialog: React.FC<DeletePackageDialogProps> = ({
  open,
  onClose,
  onConfirm,
  package: pkg,
  loading = false,
}) => {
  if (!pkg) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          <Typography variant="h6">Delete Package</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete the package "{pkg.name}"?
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          {pkg.description}
        </Typography>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> This action cannot be undone. Deleting this package will:
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>Remove the package from all future bookings</li>
            <li>Make it unavailable for new appointments</li>
            <li>Affect any ongoing promotions using this package</li>
          </ul>
          <Typography variant="body2">
            Consider deactivating the package instead if you want to keep historical data.
          </Typography>
        </Alert>

        {pkg.isActive && (
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">
              This package is currently <strong>active</strong>. You may want to deactivate it first 
              to prevent new bookings before deletion.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="error" 
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Deleting...' : 'Delete Package'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletePackageDialog;